import { useGetBusinessPartnerById } from '@/api/business-partner/business-partner.hooks';
import { useItemFacets } from '@/api/items/items.hooks';
import { useCatalogFilterStore } from '@/stores/catalog-filter-store';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import {
  Cable,
  Laptop,
  Smartphone,
  Tablet,
  Watch,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ReactNode } from 'react';

const CATEGORY_ICONS: Record<string, ReactNode> = {
  Smartphone: <Smartphone sx={{ fontSize: 18 }} />,
  Tablet: <Tablet sx={{ fontSize: 18 }} />,
  Smartwatch: <Watch sx={{ fontSize: 18 }} />,
  Notebook: <Laptop sx={{ fontSize: 18 }} />,
  'Zubehör': <Cable sx={{ fontSize: 18 }} />,
};

export function CategoryTilesSection() {
  const { data: businessPartner } = useGetBusinessPartnerById();
  const { data: facets, isLoading } = useItemFacets(
    businessPartner?.defaultPriceListId
      ? { priceListId: businessPartner.defaultPriceListId }
      : undefined,
  );
  const { clearAll, setItemGroup, setManufacturer } = useCatalogFilterStore();
  const navigate = useNavigate();

  const handleCategoryClick = (id: number, name: string) => {
    clearAll();
    setItemGroup(id, name);
    navigate('/bp/c/items');
  };

  const handleManufacturerClick = (id: number, name: string) => {
    clearAll();
    setManufacturer(id, name);
    navigate('/bp/c/items');
  };

  const visibleCategories = (facets?.itemGroupFacets ?? [])
    .filter((f) => f.count > 20)
    .sort((a, b) => b.count - a.count);

  const visibleManufacturers = (facets?.manufacturerFacets ?? [])
    .sort((a, b) => b.count - a.count);

  return (
    <Stack spacing={1.5}>
      {/* Manufacturers */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {isLoading
          ? [...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={110}
                height={36}
                sx={{ borderRadius: 2 }}
              />
            ))
          : visibleManufacturers.map((m) => (
              <ButtonBase
                key={m.id}
                onClick={() => handleManufacturerClick(m.id, m.name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(2, 52, 76, 0.04)',
                    boxShadow: '0 2px 8px rgba(2, 52, 76, 0.08)',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                >
                  {m.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {m.count}
                </Typography>
              </ButtonBase>
            ))}
      </Stack>

      {/* Categories */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {isLoading
          ? [...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={130}
                height={36}
                sx={{ borderRadius: 2 }}
              />
            ))
          : visibleCategories.map((facet) => (
              <ButtonBase
                key={facet.name}
                onClick={() => handleCategoryClick(facet.id, facet.name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(2, 52, 76, 0.04)',
                    boxShadow: '0 2px 8px rgba(2, 52, 76, 0.08)',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', display: 'flex' }}>
                  {CATEGORY_ICONS[facet.name] ?? <Smartphone sx={{ fontSize: 18 }} />}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                >
                  {facet.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {facet.count}
                </Typography>
              </ButtonBase>
            ))}
      </Stack>
    </Stack>
  );
}
